// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// What a device's numbers are, and what they are allowed to be (M9-03, 11 §3.1).
//
// 11 §3.1: "every field is a slider or a picker where a choice will do, and is bounded to its valid
// range". This is the table that makes that true — one row per editable member of each device kind,
// carrying the bound, the step, the unit it is *shown* in, and a sentence about what it does.
//
// **The bounds are `validate.ts`'s bounds, and a test proves it rather than a comment claiming it.**
// `fields.test.ts` takes every row here, builds a real device with that field at its minimum, at its
// maximum, and a step outside each end, and runs the **real validator** over all four. Accept,
// accept, refuse, refuse. That is a stronger statement than sharing a constant would be: sharing one
// proves the two files hold the same number, and this proves the number is the one the validator
// actually enforces — which is the thing an author is protected by.
//
// **Shown in the unit an author thinks in, stored in the unit the format uses.** A playfield is
// authored in millimetres and stored in metres; an angle is dialled in degrees and stored in
// radians; a spinner vane is weighed in grams and stored in kilograms. The conversion lives here,
// once, in both directions — because a panel that showed metres would put a conversion between the
// decision and the person making it, and a format that stored millimetres would disagree with every
// other file in the product.
//
// **A direction is an angle, not two numbers.** `exit.direction` has to be non-zero and
// `passDirection` has to be a unit vector; both are guaranteed by construction if the author dials a
// bearing and the vector is computed from it. Two free number boxes can express neither constraint
// without a validation message, and a validation message is what this whole surface exists to make
// impossible.
//
// **`passDirection` is a side, not a bearing.** It must also be perpendicular to the gate's own
// face, so the only free choice is *which* of the two perpendiculars — which is a choice, and 11
// §3.1 asks for a picker wherever one will do.
//
// Pure: no DOM, no clock. It reads and writes plain device objects.

import {type EditorPart as MachineDevice,type PartKind as DeviceKind} from './parts'
import type {   Point2, WireLayer } from '../../core/ts/machine'

/**
 * What the table reads and writes: a device, or a wire (the bench's follow-up, 26 Sep 2026: "a
 * wire's capture radius on a card"). A wire is a layer, not a device, but its one number is a
 * number like any device's — a member, a unit, the validator's bound — so it is one more row of
 * the same table rather than a second table with its own conversions.
 */
export type Editable = MachineDevice | WireLayer

/** A device kind, or `wire`: what `fieldsFor` answers for. */
export type FieldKind = DeviceKind | 'wire'

/** How a value is shown against how it is stored. */
export type FieldUnit =
  /** Metres stored, millimetres shown. */
  | 'mm'
  /** Radians stored, degrees shown. */
  | 'deg'
  /** Kilograms stored, grams shown. */
  | 'g'
  /** Shown as stored. */
  | 'ms'
  | 'm/s'
  | 'N'
  | 'N·m'
  | ''

export interface NumberField {
  readonly type: 'number'
  /** The member, as 04 spells it. `exit.speed` for a nested one. */
  readonly path: string
  readonly label: string
  readonly hint: string
  readonly unit: FieldUnit
  /** In stored units, always — the bounds are the validator's own. */
  readonly min: number
  readonly max: number
  /** The step, in *shown* units, because it is what a slider moves by. */
  readonly step: number
  /** Absent is legal; the format has a default. */
  readonly optional?: boolean
}

export interface ChoiceField {
  readonly type: 'choice'
  readonly path: string
  readonly label: string
  readonly hint: string
  readonly options: readonly { readonly value: string; readonly label: string }[]
}

/** A bearing in the playfield plane, stored as a unit vector. */
export interface AngleField {
  readonly type: 'angle'
  readonly path: string
  readonly label: string
  readonly hint: string
}

export type DeviceField = NumberField | ChoiceField | AngleField

const SPEED = { unit: 'm/s' as const, step: 0.1 }
const MS = { unit: 'ms' as const, step: 10 }

/** Metres, shown as millimetres, stepping a millimetre at a time. */
const MM = { unit: 'mm' as const, step: 1 }

const FLIPPER: readonly DeviceField[] = [
  { type: 'number', path: 'length', label: 'Length', hint: 'Pivot to tip.', ...MM, min: 0.03, max: 0.15 },
  { type: 'number', path: 'radiusPivot', label: 'Pivot radius', hint: 'The fat end of the bat.', ...MM, min: 0.004, max: 0.03 },
  { type: 'number', path: 'radiusTip', label: 'Tip radius', hint: 'The thin end.', ...MM, min: 0.003, max: 0.03 },
  { type: 'number', path: 'restAngle', label: 'Rest angle', hint: 'Where the bat sits with the coil off.', unit: 'deg', step: 1, min: -7, max: 7 },
  { type: 'number', path: 'sweep', label: 'Sweep', hint: 'How far it swings when it fires.', unit: 'deg', step: 1, min: 0.2, max: 1.6 },
  {
    type: 'choice',
    path: 'handedness',
    label: 'Hand',
    hint: 'Which way the bat swings.',
    options: [
      { value: 'left', label: 'Left' },
      { value: 'right', label: 'Right' },
    ],
  },
  { type: 'number', path: 'strength', label: 'Strength', hint: 'Coil force. Higher throws the ball harder.', unit: 'N', step: 50, min: 500, max: 8000, optional: true },
  { type: 'number', path: 'returnTorque', label: 'Return spring', hint: 'What pulls the bat back down.', unit: 'N·m', step: 0.01, min: 0.01, max: 0.4, optional: true },
]

const POP_BUMPER: readonly DeviceField[] = [
  { type: 'number', path: 'skirtRadius', label: 'Skirt radius', hint: 'How wide the ball has to come to set it off.', ...MM, min: 0.01, max: 0.08 },
  { type: 'number', path: 'kickSpeed', label: 'Kick speed', hint: 'How hard it throws the ball away.', ...SPEED, min: 0.5, max: 6 },
  { type: 'number', path: 'cooldownMs', label: 'Cooldown', hint: 'How long before it can fire again.', ...MS, min: 0, max: 2000, optional: true },
]

const SLINGSHOT: readonly DeviceField[] = [
  { type: 'number', path: 'kickSpeed', label: 'Kick speed', hint: 'How hard the rubber throws the ball back.', ...SPEED, min: 0.5, max: 7 },
  { type: 'number', path: 'triggerSpeed', label: 'Trigger speed', hint: 'How hard the ball must arrive to set it off.', ...SPEED, min: 0.1, max: 7, optional: true },
]

const TARGET: readonly DeviceField[] = [
  { type: 'number', path: 'dropSpeed', label: 'Drop speed', hint: 'How fast the face falls out of the way.', ...SPEED, min: 0.1, max: 5, optional: true },
]

const BANK: readonly DeviceField[] = [
  { type: 'number', path: 'autoResetMs', label: 'Auto-reset', hint: 'How long after the last target drops before they all come back. Zero never.', ...MS, min: 0, max: 60000, optional: true },
]

const SPINNER: readonly DeviceField[] = [
  { type: 'number', path: 'vaneMass', label: 'Vane mass', hint: 'A real one is about four grams.', unit: 'g', step: 0.5, min: 0.0005, max: 0.05 },
  { type: 'number', path: 'shaftHeight', label: 'Shaft height', hint: 'How high the shaft stands above the playfield.', ...MM, min: 0.015, max: 0.08 },
  { type: 'number', path: 'vaneDrop', label: 'Vane drop', hint: 'How far the vane hangs below its shaft.', ...MM, min: 0.005, max: 0.07 },
  { type: 'number', path: 'damping', label: 'Damping', hint: 'How quickly it stops spinning.', unit: '', step: 0.0001, min: 0, max: 20 },
]

const KICKER: readonly DeviceField[] = [
  { type: 'number', path: 'captureRadius', label: 'Capture radius', hint: 'How near the ball must roll to be caught.', ...MM, min: 0.008, max: 0.06 },
  { type: 'number', path: 'holdMs', label: 'Hold', hint: 'How long it keeps the ball. −1 holds until it is told to fire.', ...MS, min: -1, max: 120000 },
  { type: 'angle', path: 'exit.direction', label: 'Exit bearing', hint: 'Which way it throws the ball.' },
  { type: 'number', path: 'exit.speed', label: 'Exit speed', hint: 'How hard it throws.', ...SPEED, min: 0.1, max: 9 },
  { type: 'number', path: 'captureSpeed', label: 'Capture speed', hint: 'The fastest ball it can still catch.', ...SPEED, min: 0.1, max: 9, optional: true },
  { type: 'number', path: 'rearmMs', label: 'Re-arm', hint: 'How long before it can catch another.', ...MS, min: 0, max: 60000, optional: true },
]

const MAGNET: readonly DeviceField[] = [
  { type:'number',path:'releaseAfterMs',label:'Pull window',hint:'Pulls briefly, then releases until balls leave.',...MS,min:20,max:500,optional:true },
  { type: 'number', path: 'radius', label: 'Reach', hint: 'How far its pull is felt.', ...MM, min: 0.01, max: 0.2 },
  { type: 'number', path: 'peakForce', label: 'Peak force', hint: 'How hard it pulls at the centre.', unit: 'N', step: 0.05, min: 0.01, max: 5.25 },
  {
    type: 'choice',
    path: 'falloff',
    label: 'Falloff',
    hint: 'How the pull weakens with distance.',
    options: [
      { value: 'inverseSquare', label: 'Inverse square' },
      { value: 'linear', label: 'Linear' },
    ],
  },
]

const GATE: readonly DeviceField[] = [
  {
    type: 'choice',
    path: 'passDirection',
    label: 'Lets the ball through',
    hint: 'Which side of the gate the ball may pass towards.',
    options: [
      { value: 'forward', label: 'One way' },
      { value: 'reverse', label: 'The other' },
    ],
  },
  { type: 'number', path: 'armMass', label: 'Arm mass', hint: 'How heavy the flap is to push open.', unit: 'g', step: 0.5, min: 0.0005, max: 0.05, optional: true },
]

const ROLLOVER: readonly DeviceField[] = [
  { type: 'number', path: 'debounceMs', label: 'Debounce', hint: 'How long before it can score again.', ...MS, min: 0, max: 10000, optional: true },
]

/**
 * A habitrail (26 Sep 2026): how near the ball must pass the wire to be taken onto it. The
 * validator's *layer* bound, 5 to 60 mm — wider at the bottom than a kicker's, because a wire
 * catches a ball rolling along it and a kicker one dropping into it.
 */
const WIRE: readonly DeviceField[] = [
  { type: 'number', path: 'captureRadius', label: 'Capture radius', hint: 'How near the ball must pass the wire to be taken onto it.', ...MM, min: 0.005, max: 0.06 },
]

const CAPTIVE: readonly DeviceField[] = [
  { type: 'number', path: 'travel', label: 'Travel', hint: 'How far the captive ball can move.', ...MM, min: 0.005, max: 0.2 },
  { type: 'angle', path: 'direction', label: 'Bearing', hint: 'Which way its track runs.' },
]

/**
 * The editable numbers of one kind.
 *
 * Position is deliberately absent: a device is placed on the canvas, where a millimetre is four
 * pixels and the arrow keys are exact, and a second set of coordinate boxes would be a second place
 * for the same fact to be wrong. The readout beside the canvas already prints it.
 */
export function fieldsFor(kind: FieldKind): readonly DeviceField[] {
  switch (kind) {
    case 'flasher':return [{type:'choice',path:'colour',label:'Lens colour',hint:'Lit at ball start. Flashes briefly when the nearest shot is hit.',options:[{value:'#FFD9A0',label:'Warm white'},{value:'#FF3FA0',label:'Pink'},{value:'#3FE0FF',label:'Cyan'},{value:'#FF5028',label:'Red'}]}]
    case 'wire':
      return WIRE
    case 'flipper':
      return FLIPPER
    case 'popBumper':
      return POP_BUMPER
    case 'slingshot':
      return SLINGSHOT
    case 'standupTarget':
      return []
    case 'dropTarget':
      return TARGET
    case 'dropTargetBank':
      return BANK
    case 'spinner':
      return SPINNER
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
      return KICKER
    case 'magnet':
      return MAGNET
    case 'gate':
    case 'ballSaveGate':
    case 'diverter':
      return GATE
    case 'rollover':
      return ROLLOVER
    case 'captiveBall':
      return CAPTIVE
  }
}

/** Stored → shown. Metres become millimetres, radians degrees, kilograms grams. */
export function toShown(value: number, unit: FieldUnit): number {
  if (unit === 'mm' || unit === 'g') return value * 1000
  if (unit === 'deg') return (value * 180) / Math.PI
  return value
}

/** …and back, rounded so a slider cannot write a number nobody asked for. */
export function toStored(shown: number, unit: FieldUnit): number {
  if (unit === 'mm' || unit === 'g') return Number((shown / 1000).toFixed(6))
  if (unit === 'deg') return Number(((shown * Math.PI) / 180).toFixed(6))
  return Number(shown.toFixed(6))
}

/** The bound in the unit the control is drawn in. */
export function shownRange(field: NumberField): { readonly min: number; readonly max: number } {
  const a = toShown(field.min, field.unit)
  const b = toShown(field.max, field.unit)
  return { min: Math.min(a, b), max: Math.max(a, b) }
}

function member(device: Editable, path: string): unknown {
  const record = device as unknown as Record<string, unknown>
  const dot = path.indexOf('.')
  if (dot === -1) return record[path]
  const inner = record[path.slice(0, dot)]
  if (typeof inner !== 'object' || inner === null) return undefined
  return (inner as Record<string, unknown>)[path.slice(dot + 1)]
}

/** The stored value of a field, or undefined when an optional one is absent. */
export function readNumber(device: Editable, field: NumberField): number | undefined {
  const value = member(device, field.path)
  return typeof value === 'number' ? value : undefined
}

export function readChoice(device: Editable, field: ChoiceField): string | undefined {
  if (field.path === 'passDirection') return 'forward'
  const value = member(device, field.path)
  return typeof value === 'string' ? value : undefined
}

/** A stored direction as a bearing in degrees, measured from +x towards +y. */
export function readAngle(device: Editable, field: AngleField): number {
  const value = member(device, field.path)
  if (!Array.isArray(value) || value.length < 2) return 0
  const [x, y] = value as [number, number]
  return (Math.atan2(y, x) * 180) / Math.PI
}

function written<T extends Editable>(device: T, path: string, value: unknown): T {
  const record = device as unknown as Record<string, unknown>
  const dot = path.indexOf('.')
  if (dot === -1) return { ...record, [path]: value } as unknown as T
  const head = path.slice(0, dot)
  const inner = record[head]
  const base = typeof inner === 'object' && inner !== null ? (inner as Record<string, unknown>) : {}
  return { ...record, [head]: { ...base, [path.slice(dot + 1)]: value } } as unknown as T
}

/**
 * Write one number, clamped to its own bound.
 *
 * **Clamped rather than refused.** The control cannot express an out-of-range value — a slider stops
 * at its end and a number box carries `min` and `max` — so a value outside one can only arrive from
 * a keyboard typing past the end, and the useful answer there is the end rather than a message.
 */
export function writeNumber<T extends Editable>(device: T, field: NumberField, stored: number): T {
  const clamped = Math.min(Math.max(stored, field.min), field.max)
  return written(device, field.path, clamped)
}

export function writeChoice<T extends Editable>(device: T, field: ChoiceField, value: string): T {
  if (field.path === 'handedness') return writeHandedness(device, value)
  if (field.path !== 'passDirection') return written(device, field.path, value)
  // **A side, not a vector.** The stored normal must stay perpendicular to the face, so "the other
  // one" is a negation rather than a value the author types — and negating a unit vector leaves a
  // unit vector, which is the second thing the validator requires.
  if (value !== 'reverse') return device
  const current = member(device, 'passDirection')
  if (!Array.isArray(current) || current.length < 2) return device
  const [x, y] = current as [number, number]
  return written(device, 'passDirection', [tidy(-x), tidy(-y)] as Point2)
}

/**
 * **A flipper's side is a mirror, not a label** (the owner's round on build 92, 26 Sep 2026: "no
 * obvious way to change the flipper orientation"). `restAngle` is the bat's absolute direction —
 * a left bat points right and down (−21°), a right bat left and down (201°) — and `handedness`
 * only says which way it swings. Changing the side alone left the bat pointing where it was and
 * swinging *away* from the ball. So the other side is the bat mirrored across the playfield's
 * long axis: its rest angle becomes π − itself, kept inside the validator's ±7 rad.
 */
function writeHandedness<T extends Editable>(device: T, value: string): T {
  const current = member(device, 'handedness')
  if (value === current) return device
  const withSide = written(device, 'handedness', value)
  const rest = member(device, 'restAngle')
  if (typeof rest !== 'number') return withSide
  let mirrored = Math.PI - rest
  while (mirrored > 7) mirrored -= 2 * Math.PI
  while (mirrored < -7) mirrored += 2 * Math.PI
  return written(withSide, 'restAngle', tidy(mirrored))
}

/** A bearing in degrees, stored as the unit vector it names. */
export function writeAngle<T extends Editable>(device: T, field: AngleField, degrees: number): T {
  const radians = (degrees * Math.PI) / 180
  return written(device, field.path, [tidy(Math.cos(radians)), tidy(Math.sin(radians))] as Point2)
}

function tidy(value: number): number {
  return Number(value.toFixed(6))
}
